/**
 * The lines of a page, in order (#376), with text-identical pairs asked about once (#392).
 *
 * A pair is two lines whose `identicalPartnerRegionId` name each other. While NEITHER has
 * an entry and the learner has not chosen "show them separately", the pair is drawn as ONE
 * card at the first line's position (`pair-review.tsx`) and the second is not drawn on its
 * own. As soon as either has an entry - or the learner chose to see them apart - both are
 * ordinary cards (`region-review.tsx`), so nothing a learner has said is ever hidden behind
 * the paired card. Line numbers are the lines' own positions, so a paired card reads
 * "Lines 3 and 4" and the next card is Line 5.
 *
 * One line at a time, and a tap moves on (#660). The steps are the cards that still need the learner. Pressing a reading
 * records it (the card's own call, unchanged) and, once the server has it, the list moves to the next line: no Next button.
 * The step is chosen from what the server says (`resolved` on the cards), so a line that fails to save stays on screen with
 * its error, and nothing advances past a save that did not happen. A quiet Back appears from the second step and walks back
 * through the lines the learner has been on; a line answered already shows its question again, and pressing a reading (or
 * Cancel) moves on. This is PRESENTATION ONLY: every card stays mounted - merely `hidden` - so a half-typed box, its editing
 * flag (which the page reads to refuse Confirm), a pending save and every request any control makes are exactly what they
 * are in the full list. "Show all lines" draws that full list, as before.
 *
 * "A quick look" (#660) is the page's final step, and the only place the page's confirm is drawn. It shows EVERY line a
 * reader settled (what is presented is what is graded), one compact row each: a small picture of the line, the text the
 * readers read, and Change, which opens the line's own card with "Fix the reading" open. Whenever confirm can be pressed,
 * every such line is on screen: on the glance, or as its open card while it is being changed. A page with no line that
 * needs the learner opens on the glance; a page with no reader-settled line goes from the last question to its confirm
 * with no empty glance. Lines left out of the check (#465) are graded as nothing, so they are not among the rows.
 */

import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import type { HomeworkTranscriptionResponse } from "@davinci/contracts";
import { useHomeworkLabels } from "../../i18n/homework-labels.js";
import { RegionCrop, type PageSize } from "./region-crop.js";
import type { CheckStep } from "./check-row.js";
import { PairReview } from "./pair-review.js";
import { QuestionLabel, shownQuestion } from "./question-label.js";
import { RegionReview } from "./region-review.js";
import { HandwrittenText } from "./untrusted-text.js";

type Region = HomeworkTranscriptionResponse["regions"][number];

type Card =
  | { readonly kind: "region"; readonly region: Region; readonly index: number }
  | {
      readonly kind: "pair";
      readonly first: Region;
      readonly second: Region;
      readonly firstIndex: number;
      readonly secondIndex: number;
    };

/** The cards the list draws, in order: a text-identical pair with no entry yet is one card; anything else is one card per line. */
function cardsOf(
  regions: readonly Region[],
  separate: ReadonlySet<string>,
): Card[] {
  const indexOf = new Map(
    regions.map((region, index) => [region.regionId, index]),
  );
  const cards: Card[] = [];
  regions.forEach((region, index) => {
    const partnerId = region.identicalPartnerRegionId;
    const partnerIndex =
      partnerId === null ? undefined : indexOf.get(partnerId);
    const partner =
      partnerIndex === undefined ? undefined : regions[partnerIndex];
    const asPair =
      partner !== undefined &&
      partner.identicalPartnerRegionId === region.regionId &&
      region.learnerEntry === undefined &&
      partner.learnerEntry === undefined;
    if (asPair && partnerIndex !== undefined) {
      const first = index < partnerIndex ? region : partner;
      const second = index < partnerIndex ? partner : region;
      if (!separate.has(first.regionId)) {
        if (index > partnerIndex) return; // drawn by the card at the first line's position
        cards.push({
          kind: "pair",
          first,
          second,
          firstIndex: index,
          secondIndex: partnerIndex,
        });
        return;
      }
    }
    cards.push({ kind: "region", region, index });
  });
  return cards;
}

const anchorOf = (card: Card): string =>
  card.kind === "pair" ? card.first.regionId : card.region.regionId;

/** A card needs the learner while any line on it is unresolved (#641): the stepper walks only these. */
const needsLearner = (card: Card): boolean =>
  card.kind === "pair"
    ? !card.first.resolved || !card.second.resolved
    : !card.region.resolved;

/**
 * A card a READER settled (#641, #660): resolved, no entry of the learner's stands, and its source is not one of the
 * learner's (`learner_...`), decided from what the server says and never from a list of readers' sources. It is graded as
 * read, so it is a row on the quick look. A line left out of the check (`readNotChecked`) is graded as nothing, so it is
 * not one of these; "Show all lines" still reaches it.
 */
const readerSettled = (card: Card): card is Extract<Card, { kind: "region" }> =>
  card.kind === "region" &&
  card.region.resolved &&
  card.region.readNotChecked !== true &&
  card.region.learnerEntry === undefined &&
  card.region.effectiveSource?.startsWith("learner_") !== true;

/**
 * A line the learner skipped (#658, #660): settled and left out of the check, with no text. Its own row on the quick look,
 * in its own words, so every settled line is on screen whenever the page's confirm can be pressed.
 */
const skippedCard = (card: Card): boolean =>
  card.kind === "region" &&
  card.region.resolved &&
  card.region.learnerEntry?.kind === "skipped";

/** What the readers read for a settled line: the text the check will use. */
const settledText = (region: Region): string =>
  region.effectiveText ??
  region.agreedText ??
  (region.primaryReading ?? region.secondReading)?.text ??
  "";

export interface RegionListProps {
  readonly regions: readonly Region[];
  readonly pageId: string;
  readonly uploadId: string;
  readonly pageSize: PageSize;
  readonly onEditingChange: (regionId: string, editing: boolean) => void;
  /** What follows the last line (the page's confirm panel). Told whether the learner is at the end, or sees every line. */
  readonly after?: (atEnd: boolean) => React.ReactNode;
  /** False draws the whole list with no stepper, as before #619 (the static render the visual test takes). Default true. */
  readonly stepper?: boolean;
  /**
   * The row above the lines (#648): "Page 2 · Question 1" and the step dots. Told which step is showing, if the list is
   * stepping, and whether it is on the quick look.
   */
  readonly row?: (
    step: CheckStep | undefined,
    onGlance: boolean,
  ) => React.ReactNode;
  /** Quiet facts at the foot of the screen (#648): what still needs the learner, and the way back to the summary. */
  readonly quietStart?: React.ReactNode;
  readonly quietEnd?: React.ReactNode;
}

export function RegionList({
  regions,
  pageId,
  uploadId,
  pageSize,
  onEditingChange,
  after,
  stepper = true,
  row,
  quietStart,
  quietEnd,
}: RegionListProps): React.JSX.Element {
  const t = useHomeworkLabels();
  // Pairs the learner chose to see apart, by the first region's id (this visit only: nothing is recorded).
  const [separate, setSeparate] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  // Presentation state only (#619, #660).
  const [showAll, setShowAll] = useState(false);
  const [anchor, setAnchor] = useState<string | undefined>(undefined);
  // The steps the learner has been on and left, oldest first; null is the quick look. Back walks it.
  const [trail, setTrail] = useState<readonly (string | null)[]>([]);
  // The card on screen is one the learner came back to (Back): it stays until they answer it or leave it.
  const [revisit, setRevisit] = useState(false);
  // Lines a request to open was made for (Back: ask again; Change on the quick look: the editor), and how many times.
  const [requests, setRequests] = useState<
    ReadonlyMap<string, { mode: "ask" | "editor"; id: number }>
  >(() => new Map());
  // The lines showing an editor with something to save, so a card changed from the quick look stays on screen.
  const [editing, setEditing] = useState<ReadonlySet<string>>(() => new Set());
  const listRef = useRef<HTMLOListElement>(null);
  const glanceTitle = useRef<HTMLHeadingElement>(null);
  const goingBack = useRef(false);
  const lastView = useRef<string | null | undefined>(undefined);
  const focusNext = useRef(false);
  // Every line that has needed the learner in this visit: the dots' length stays put as lines are answered.
  const everNeeded = useRef(new Set<string>());

  // Stable, because every card's effects depend on it: it tells the page, and keeps its own record of open editors.
  const trackEditing = useCallback(
    (regionId: string, isEditing: boolean): void => {
      onEditingChange(regionId, isEditing);
      setEditing((existing) => {
        if (existing.has(regionId) === isEditing) return existing;
        const next = new Set(existing);
        if (isEditing) next.add(regionId);
        else next.delete(regionId);
        return next;
      });
    },
    [onEditingChange],
  );

  // A glance row's label is a second view of the line's label: its own key, so it never clobbers the card's.
  const trackGlanceEditing = useCallback(
    (key: string, isEditing: boolean): void => trackEditing(`glance:${key}`, isEditing),
    [trackEditing],
  );

  const cards = cardsOf(regions, separate);
  const stepping = stepper && !showAll;
  const needing = cards.filter(needsLearner);
  needing.forEach((card) => everNeeded.current.add(anchorOf(card)));

  // The step on screen. A line that still needs the learner stays; one that was just answered gives way to the next line
  // that needs them (after it in page order, else from the top); with none left it is the quick look.
  const anchorIndex = cards.findIndex((card) => anchorOf(card) === anchor);
  const anchored = anchorIndex === -1 ? undefined : cards[anchorIndex];
  let current: Card | undefined;
  if (stepping) {
    if (anchored !== undefined && (revisit || needsLearner(anchored)))
      current = anchored;
    else
      current =
        cards.slice(anchorIndex + 1).find(needsLearner) ??
        cards.find(needsLearner);
  }
  const currentAnchor = current === undefined ? undefined : anchorOf(current);
  const onGlance = stepping && current === undefined;
  const viewKey = stepping ? (currentAnchor ?? null) : undefined;

  // Keep the anchor on the line on screen, and remember where the learner has been so Back can return there.
  useEffect(() => {
    const previous = lastView.current;
    lastView.current = viewKey;
    if (viewKey === undefined || previous === viewKey) return;
    if (currentAnchor !== undefined) setAnchor(currentAnchor);
    if (previous === undefined) return;
    focusNext.current = true;
    if (goingBack.current) {
      goingBack.current = false;
      return;
    }
    setTrail((history) => [...history, previous]);
  }, [viewKey, currentAnchor]);

  // After a step, focus moves to the new line's heading (or the quick look's title), so a keyboard or screen-reader learner lands on it.
  useEffect(() => {
    if (!focusNext.current) return;
    focusNext.current = false;
    const target = onGlance
      ? glanceTitle.current
      : listRef.current?.querySelector<HTMLElement>("li:not([hidden]) > h3");
    if (target === null || target === undefined) return;
    target.tabIndex = -1;
    target.focus();
  }, [viewKey, onGlance]);

  const settled = cards.filter(readerSettled);
  const step: CheckStep | undefined =
    stepping && current !== undefined
      ? (() => {
          const walk = cards.filter(
            (card) =>
              everNeeded.current.has(anchorOf(card)) ||
              anchorOf(card) === currentAnchor,
          );
          return {
            index: Math.max(
              0,
              walk.findIndex((card) => anchorOf(card) === currentAnchor),
            ),
            count: Math.max(1, walk.length),
            after: needing.filter((card) => anchorOf(card) !== currentAnchor)
              .length,
            label:
              current.kind === "pair"
                ? shownQuestion(current.first)
                : shownQuestion(current.region),
          };
        })()
      : undefined;

  const back = trail.length === 0 ? undefined : trail[trail.length - 1];
  const goBack = (): void => {
    if (back === undefined) return;
    goingBack.current = true;
    focusNext.current = true;
    setTrail((history) => history.slice(0, -1));
    if (back === null) {
      setRevisit(false);
      setAnchor(undefined);
      return;
    }
    // The question is asked again on a line already answered; pressing a reading, or Cancel, moves on.
    setRevisit(true);
    setAnchor(back);
    setRequests((existing) =>
      new Map(existing).set(back, {
        mode: "ask",
        id: (existing.get(back)?.id ?? 0) + 1,
      }),
    );
  };
  const moveOn = (regionId: string): void => {
    if (regionId === anchor) setRevisit(false);
  };
  // Change on the quick look: the line's own card, with "Fix the reading" open.
  const changeFromGlance = (regionId: string): void =>
    setRequests((existing) =>
      new Map(existing).set(regionId, {
        mode: "editor",
        id: (existing.get(regionId)?.id ?? 0) + 1,
      }),
    );

  // A card is drawn when it is the step on screen, or - on the quick look - while its editor is open (Change).
  const visible = (card: Card): boolean =>
    !stepping ||
    (currentAnchor !== undefined && anchorOf(card) === currentAnchor) ||
    (onGlance && editing.has(anchorOf(card)));
  // The quick look is the lines a reader settled and the lines the learner skipped, in the page's order.
  const glanceRows = onGlance
    ? cards.filter(
        (card): card is Extract<Card, { kind: "region" }> =>
          (readerSettled(card) || skippedCard(card)) &&
          !editing.has(anchorOf(card)),
      )
    : [];
  // What the readers did is said only of the lines they settled, and only when true of every one.
  const readRows = glanceRows.filter(readerSettled);
  const allAgreed = readRows.every(
    (card) => card.region.agreement === "agreed",
  );

  return (
    <>
      {row?.(step, onGlance)}
      <ol className="region-list" ref={listRef}>
        {cards.map((card) => {
          const hidden = !visible(card);
          const onBack =
            stepping && back !== undefined && anchorOf(card) === currentAnchor
              ? { onBack: goBack }
              : {};
          if (card.kind === "pair") {
            return (
              <PairReview
                key={`pair-${card.first.regionId}`}
                first={card.first}
                second={card.second}
                firstLineNumber={card.firstIndex + 1}
                secondLineNumber={card.secondIndex + 1}
                pageId={pageId}
                uploadId={uploadId}
                pageSize={pageSize}
                onShowSeparately={() =>
                  setSeparate((previous) =>
                    new Set(previous).add(card.first.regionId),
                  )
                }
                hidden={hidden}
                onMoveOn={moveOn}
                {...onBack}
              />
            );
          }
          const request = requests.get(card.region.regionId);
          return (
            <Fragment key={card.region.regionId}>
              <RegionReview
                region={card.region}
                lineNumber={card.index + 1}
                pageId={pageId}
                uploadId={uploadId}
                pageSize={pageSize}
                onEditingChange={trackEditing}
                hidden={hidden}
                onMoveOn={moveOn}
                {...(request === undefined ? {} : { openRequest: request })}
                {...onBack}
              />
            </Fragment>
          );
        })}
      </ol>
      {onGlance && glanceRows.length > 0 ? (
        <section className="glance" aria-labelledby="quick-look">
          <div className="glance-head">
            <div>
              <h3 id="quick-look" ref={glanceTitle}>
                {t.glanceTitle}
              </h3>
              {/* Only said when it is true of every row: a line one reader read, or settled by its context, was not agreed on. */}
              {readRows.length === 0 ? null : (
                <p>{allAgreed ? t.glanceSubAgreed : t.glanceSubRead}</p>
              )}
            </div>
            {back === undefined ? null : (
              <button type="button" className="button-link" onClick={goBack}>
                {t.previousLine}
              </button>
            )}
          </div>
          <ul className="glance-list">
            {glanceRows.map((card) => (
              <li key={card.region.regionId} className="glance-row">
                <div className="glance-picture">
                  <RegionCrop
                    pageId={pageId}
                    bbox={card.region.bbox}
                    approximate={card.region.bboxApproximate}
                    lineNumber={card.index + 1}
                    text={settledText(card.region)}
                    boxHeightPx={26}
                    {...pageSize}
                  />
                </div>
                <div className="glance-text">
                  {skippedCard(card) ? (
                    <p className="glance-skipped">{t.skippedState}</p>
                  ) : (
                    <HandwrittenText text={settledText(card.region)} />
                  )}
                  <button
                    type="button"
                    className="button-link"
                    aria-label={t.changeLineN(card.index + 1)}
                    onClick={() => changeFromGlance(card.region.regionId)}
                  >
                    {t.glanceChange}
                  </button>
                </div>
                {/* The confirm's digest covers the question label, so what is confirmed shows it (#660). */}
                {skippedCard(card) ? null : (
                  <div className="glance-label">
                    <QuestionLabel
                      region={card.region}
                      pageId={pageId}
                      uploadId={uploadId}
                      onEditingChange={trackGlanceEditing}
                    />
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      ) : onGlance && back !== undefined ? (
        <div className="glance-head">
          <button type="button" className="button-link" onClick={goBack}>
            {t.previousLine}
          </button>
        </div>
      ) : null}
      {after?.(!stepping || onGlance)}
      <div className="check-quiet">
        {quietStart}
        {/* A line the learner answered or left out has no row on the quick look, so a page with nothing else offers the full list too. */}
        {stepper &&
        (cards.length > 1 || showAll || (onGlance && settled.length === 0 && !cards.some(skippedCard))) ? (
          <>
            <span>{t.linesOnPage(regions.length)}</span>
            <button
              type="button"
              className="button-link"
              onClick={() => setShowAll((value) => !value)}
            >
              {showAll ? t.showOneAtATime : t.showAllLines}
            </button>
          </>
        ) : null}
        {quietEnd}
      </div>
    </>
  );
}
